import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { Mic, Calculator, WifiOff, Award, Languages, Link2 } from 'lucide-react';

const features = [
  {
    icon: Mic,
    title: 'Ambient AI Scribe',
    desc: 'Listens to the full visit and writes the note. No typing required.',
  },
  {
    icon: Calculator,
    title: 'Auto ICD-10 + CPT Coding',
    desc: 'Generates the exact billing codes automatically. Clinics get paid faster and claim denials drop.',
  },
  {
    icon: WifiOff,
    title: 'Offline-First',
    desc: 'Optimized for low-bandwidth cellular. Works in remote clinics where internet is unreliable. No expensive hardware required.',
  },
  {
    icon: Award,
    title: 'Section 330 Ready',
    desc: 'Built specifically for Rural Health Center requirements. The only AI scribe that qualifies for federal grant funding.',
  },
  {
    icon: Languages,
    title: 'Multilingual',
    desc: 'Real-time translation and patient summaries in Spanish and Hmong. Built for Midwest agricultural communities.',
  },
  {
    icon: Link2,
    title: 'EHR Integration',
    desc: 'Connects directly with Epic and Cerner. No duplicate data entry.',
  },
];

export default function FeaturesGrid() {
  const [ref, isVisible] = useScrollAnimation(0.1);

  return (
    <section id="features" data-testid="features-grid" className="py-24 md:py-32 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className={`scroll-fade-up ${isVisible ? 'visible' : ''}`}>
          <p className="uppercase text-xs tracking-[0.2em] text-vf-forest font-body font-semibold mb-4 text-center">
            Features
          </p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-vf-charcoal tracking-tight mb-4 text-center leading-tight">
            Everything your clinic needs.
          </h2>
          <p className="font-body text-base md:text-lg text-vf-charcoal/60 text-center max-w-xl mx-auto mb-16">
            Built from the ground up for rural healthcare — not retrofitted from enterprise software.
          </p>
        </div>

        <div className={`stagger-children ${isVisible ? 'visible' : ''} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6`}>
          {features.map((feat) => (
            <div
              key={feat.title}
              data-testid="feature-card"
              className="bg-white border border-vf-sage p-8 hover:-translate-y-1 hover:shadow-sm transition-all duration-300 group"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-vf-mint/30 mb-6 group-hover:bg-vf-mint/50 transition-colors duration-300">
                <feat.icon size={24} strokeWidth={1.5} className="text-vf-forest" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-vf-charcoal mb-2">
                {feat.title}
              </h3>
              <p className="font-body text-sm text-vf-charcoal/60 leading-relaxed">
                {feat.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
